/**
 * The application shell: a 64px toolbar, never a sidebar.
 *
 * The panel's destinations are a flat handful, and its screens are tables —
 * a permanent side column would spend width every table needs, to assert a
 * hierarchy that does not exist. The second level, where there is one (inside
 * a المنشأة), is a second bar directly under this one — `SubToolbar` in
 * `context-bar.tsx` — never a dropdown here.
 *
 *   <AppShell toolbar={<Toolbar brand items actions account />}>
 *     <Routes>…</Routes>
 *   </AppShell>
 *
 * Below 900px the nav collapses behind a menu button; the brand and the
 * account menu stay.
 */
import { CaretDown, List, Moon, SignOut, Sun, X, type Icon } from '@phosphor-icons/react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';

import { initialsOf } from '@/lib/format';
import { useTheme } from '@/lib/theme';

import { IconButton } from './button';
import { Avatar } from './feedback';
import { Menu } from './overlay';
import { cx, ui } from './text';

/* ── Types ────────────────────────────────────────────────────────── */

export interface NavLeaf {
  to: string;
  label: string;
  icon?: Icon;
  /** Match the path exactly (for `/`). */
  end?: boolean;
  /** One line under the label inside a dropdown group. */
  description?: string;
}

export interface NavGroup {
  label: string;
  icon?: Icon;
  children: NavLeaf[];
}

export type NavItem = NavLeaf | NavGroup;

function isGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

function isActive(pathname: string, leaf: NavLeaf): boolean {
  return matchPath({ path: leaf.to, end: leaf.end ?? false }, pathname) !== null;
}

/* ── AppShell ─────────────────────────────────────────────────────── */

export function AppShell({ toolbar, children }: { toolbar: ReactNode; children: ReactNode }) {
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {ui('skipToContent')}
      </a>
      {toolbar}
      <main className="shell-main" id="main" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}

/* ── Toolbar ──────────────────────────────────────────────────────── */

export interface ToolbarProps {
  /** The logo, as a link home. */
  brand: ReactNode;
  /** The surface's name beside the logo: "لوحة المنصّة". Hidden on narrow screens. */
  product?: string;
  items: NavItem[];
  /** Small controls before the account menu — ThemeToggle, a help link. */
  actions?: ReactNode;
  account?: ReactNode;
}

export function Toolbar({ brand, product, items, actions, account }: ToolbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const panelId = useId();

  // Any navigation closes the mobile panel.
  useEffect(() => setMobileOpen(false), [pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  return (
    <header className="toolbar">
      <div className="toolbar-inner">
        <IconButton
          className="toolbar-button toolbar-burger"
          label={mobileOpen ? ui('closeMenu') : ui('openMenu')}
          icon={mobileOpen ? X : List}
          aria-expanded={mobileOpen}
          aria-controls={panelId}
          onClick={() => setMobileOpen((open) => !open)}
        />

        <div className="toolbar-brand">
          {brand}
          {product === undefined ? null : <span className="toolbar-product">{product}</span>}
        </div>

        <nav className="toolbar-nav" aria-label={ui('mainNav')}>
          <ul className="toolbar-list">
            {items.map((item) => (
              <li key={item.label}>{isGroup(item) ? <NavDropdown group={item} pathname={pathname} /> : <ToolbarLink leaf={item} />}</li>
            ))}
          </ul>
        </nav>

        <div className="toolbar-end">
          {actions}
          {account}
        </div>
      </div>

      {mobileOpen ? (
        <nav className="toolbar-mobile" id={panelId} aria-label={ui('mainNav')}>
          <ul>
            {items.flatMap((item) =>
              isGroup(item)
                ? [
                    <li key={`h-${item.label}`} className="toolbar-mobile-heading">
                      {item.label}
                    </li>,
                    ...item.children.map((leaf) => (
                      <li key={leaf.to}>
                        <ToolbarLink leaf={leaf} mobile />
                      </li>
                    )),
                  ]
                : [
                    <li key={item.to}>
                      <ToolbarLink leaf={item} mobile />
                    </li>,
                  ],
            )}
          </ul>
        </nav>
      ) : null}
    </header>
  );
}

function ToolbarLink({ leaf, mobile = false }: { leaf: NavLeaf; mobile?: boolean }) {
  const Glyph = leaf.icon;

  return (
    <NavLink to={leaf.to} end={leaf.end ?? false} className={cx('toolbar-link', mobile && 'toolbar-link-mobile')}>
      {Glyph === undefined ? null : <Glyph size={18} aria-hidden="true" />}
      <span>{leaf.label}</span>
    </NavLink>
  );
}

/**
 * A group of destinations under one label. A disclosure (button + list of
 * links), not an ARIA menu: these are navigation, and links keep "open in new
 * tab". The trigger shows as active when any child route is.
 */
function NavDropdown({ group, pathname }: { group: NavGroup; pathname: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const active = group.children.some((leaf) => isActive(pathname, leaf));
  const Glyph = group.icon;

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointer = (event: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      className="toolbar-group"
      ref={rootRef}
      onBlur={(event) => {
        // Tabbing out of the group closes it.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        className={cx('toolbar-link', active && 'active')}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((current) => !current)}
      >
        {Glyph === undefined ? null : <Glyph size={18} aria-hidden="true" />}
        <span>{group.label}</span>
        <CaretDown className="toolbar-caret" size={12} aria-hidden="true" />
      </button>
      {open ? (
        <ul className="toolbar-dropdown" id={listId}>
          {group.children.map((leaf) => {
            const LeafGlyph = leaf.icon;

            return (
              <li key={leaf.to}>
                <NavLink to={leaf.to} end={leaf.end ?? false} className="toolbar-dropdown-link">
                  {LeafGlyph === undefined ? null : <LeafGlyph size={18} aria-hidden="true" />}
                  <span className="toolbar-dropdown-text">
                    <span>{leaf.label}</span>
                    {leaf.description === undefined ? null : <span className="toolbar-dropdown-hint">{leaf.description}</span>}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/* ── ThemeToggle ──────────────────────────────────────────────────── */

/** The label says what pressing it does — a button that leaves dark mode is not called "dark mode". */
export function ThemeToggle({ onToolbar = true }: { onToolbar?: boolean }) {
  const [theme, toggle] = useTheme();

  return (
    <IconButton
      className={onToolbar ? 'toolbar-button' : undefined}
      label={theme === 'dark' ? ui('toLight') : ui('toDark')}
      icon={theme === 'dark' ? Sun : Moon}
      onClick={toggle}
    />
  );
}

/* ── AccountMenu ──────────────────────────────────────────────────── */

export interface AccountMenuProps {
  name: string;
  email: string;
  /** Already a label: `operatorRoleLabel(role)`. */
  role: string;
  onSignOut: () => void;
  /** Pages reached from the account rather than the toolbar (platform-wide settings), above sign-out. */
  links?: { label: string; to: string; icon?: Icon; current?: boolean }[];
}

export function AccountMenu({ name, email, role, onSignOut, links = [] }: AccountMenuProps) {
  return (
    <Menu
      label={ui('account')}
      triggerClassName="toolbar-button toolbar-account"
      header={
        <div className="account-card">
          <Avatar initials={initialsOf(name)} size="lg" />
          <div className="account-text">
            <span className="account-name">{name}</span>
            <bdi className="account-email mono" dir="ltr">
              {email}
            </bdi>
            <span className="account-role">{role}</span>
          </div>
        </div>
      }
      items={[
        ...links.map((link) => ({ kind: 'link' as const, label: link.label, to: link.to, ...(link.icon === undefined ? {} : { icon: link.icon }), ...(link.current === true ? { current: true } : {}) })),
        ...(links.length === 0 ? [] : [{ kind: 'separator' as const }]),
        { label: ui('signOut'), icon: SignOut, onSelect: onSignOut, tone: 'danger' as const },
      ]}
    >
      <Avatar initials={initialsOf(name)} size="sm" />
      <span className="toolbar-account-name">{name}</span>
    </Menu>
  );
}
